import type {
  AgenticDocumentSearchResult,
  AgenticSearchSseEvent,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type {
  AgenticConversationErrorEvent,
  AgenticConversationEvent,
  AgenticConversationResponseEvent,
  AgenticConversationTurn,
  AgenticConversationTurnStatus,
  AgenticSearchConversation,
} from 'features/knowledgeDocumentViewer/types'

function createConversationEventId(): string {
  return `agentic-event-${crypto.randomUUID()}`
}

function createSubmittedQueryEvent(query: string): AgenticConversationEvent {
  return {
    id: createConversationEventId(),
    type: 'submitted_query',
    query,
  }
}

function createThinkingEvent(query: string): AgenticConversationEvent {
  return {
    id: createConversationEventId(),
    type: 'thinking',
    query,
  }
}

function createResponseEvent(
  event: AgenticDocumentSearchResult
): AgenticConversationResponseEvent {
  const { results, ...rest } = event
  return {
    ...rest,
    id: createConversationEventId(),
    type: 'response',
    resultCount: results.length,
  }
}

function createErrorEvent({
  query,
  siteId,
  conversationId,
  message,
}: {
  query: string
  siteId: string
  conversationId?: string | null
  message: string
}): AgenticConversationErrorEvent {
  return {
    id: createConversationEventId(),
    type: 'error',
    query,
    site_id: siteId,
    conversation_id: conversationId,
    message,
  }
}

function createConversationEvent(
  event: AgenticSearchSseEvent
): AgenticConversationEvent {
  if (event.type === 'response') return createResponseEvent(event)
  return {
    ...event,
    id: createConversationEventId(),
  }
}

function isTerminalEvent(event: AgenticConversationEvent): boolean {
  return event.type === 'response' || event.type === 'error'
}

function getTurnEvents(
  turn: AgenticConversationTurn
): AgenticConversationEvent[] {
  return Array.isArray(turn.events) ? turn.events : []
}

function isTerminalTurn(turn: AgenticConversationTurn): boolean {
  return getTurnEvents(turn).some(isTerminalEvent)
}

function hasDuplicateThought(
  turn: AgenticConversationTurn,
  event: AgenticConversationEvent
): boolean {
  return (
    event.type === 'thought' &&
    getTurnEvents(turn).some(
      (currentEvent) =>
        currentEvent.type === 'thought' &&
        currentEvent.message === event.message
    )
  )
}

function appendBackendEventToTurn(
  turn: AgenticConversationTurn,
  event: AgenticConversationEvent
): AgenticConversationTurn {
  if (isTerminalTurn(turn)) return turn
  if (hasDuplicateThought(turn, event)) return turn
  const currentEvents = getTurnEvents(turn)
  const baseEvents =
    currentEvents.length > 0
      ? currentEvents
      : [createSubmittedQueryEvent(turn.query)]
  return {
    ...turn,
    events: [
      ...baseEvents.filter((currentEvent) => currentEvent.type !== 'thinking'),
      event,
    ],
  }
}

export function getAgenticConversationTurnStatus(
  turn: AgenticConversationTurn
): AgenticConversationTurnStatus {
  const events = getTurnEvents(turn)
  if (events.some((event) => event.type === 'response')) return 'complete'
  if (events.some((event) => event.type === 'error')) return 'error'
  return 'loading'
}

export function hasAgenticConversationEvents(
  turn: AgenticConversationTurn
): boolean {
  return getTurnEvents(turn).length > 0
}

export function createAgenticConversationTurn({
  turnId,
  query,
}: {
  turnId: string
  query: string
}): AgenticConversationTurn {
  return {
    id: turnId,
    query,
    events: [createSubmittedQueryEvent(query), createThinkingEvent(query)],
  }
}

export function createCompletedAgenticConversationTurn({
  turnId,
  result,
}: {
  turnId: string
  result: AgenticDocumentSearchResult
}): AgenticConversationTurn {
  return {
    id: turnId,
    query: result.query,
    events: [
      createSubmittedQueryEvent(result.query),
      createResponseEvent(result),
    ],
  }
}

export function createErroredAgenticConversationTurn({
  turnId,
  query,
  siteId,
}: {
  turnId: string
  query: string
  siteId: string
}): AgenticConversationTurn {
  return {
    id: turnId,
    query,
    events: [
      createSubmittedQueryEvent(query),
      createErrorEvent({
        query,
        siteId,
        message: 'Search failed. Try again.',
      }),
    ],
  }
}

export function appendAgenticStreamEventToConversation({
  conversation,
  streamEvent,
  siteId,
}: {
  conversation: AgenticSearchConversation
  streamEvent: AgenticSearchSseEvent
  siteId: string
}): AgenticSearchConversation {
  const activeTurnId = conversation.activeTurnId
  if (!activeTurnId) return conversation
  if (streamEvent.site_id !== siteId) return conversation

  let changed = false
  const conversationEvent = createConversationEvent(streamEvent)
  const turns = conversation.turns.map((turn) => {
    if (turn.id !== activeTurnId) return turn
    if (turn.query !== streamEvent.query) return turn
    const updatedTurn = appendBackendEventToTurn(turn, conversationEvent)
    if (updatedTurn === turn) return turn
    changed = true
    return updatedTurn
  })

  if (!changed) return conversation

  const conversationId = streamEvent.conversation_id ?? conversation.id
  return {
    ...conversation,
    ...(conversationId ? { id: conversationId } : {}),
    turns,
  }
}

export function markAgenticConversationTurnError({
  conversation,
  turnId,
  siteId,
}: {
  conversation: AgenticSearchConversation
  turnId: string
  siteId: string
}): AgenticSearchConversation {
  let changed = false
  const turns = conversation.turns.map((turn) => {
    if (turn.id !== turnId) return turn
    const errorEvent = createErrorEvent({
      query: turn.query,
      siteId,
      conversationId: conversation.id,
      message: 'Search failed. Try again.',
    })
    const updatedTurn = appendBackendEventToTurn(turn, errorEvent)
    if (updatedTurn === turn) return turn
    changed = true
    return updatedTurn
  })

  if (!changed) return conversation
  return {
    ...conversation,
    turns,
  }
}
